import React, { useEffect, useCallback } from 'react';
import { useFormikContext } from 'formik';
import { CheckoutValues } from 'types/payments';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import Box from '@mui/material/Box';
import * as IconMDI from 'mdi-material-ui';
import useStyles from './styles';
import CreditCardForm from './CreditCardForm';
import { CardSectionHeader, PaymentType, RadioCard } from '@elevator/ui';

/**
 * CheckoutForm Component
 *
 * Payment method selection for checkout flows (submit and campaign). Renders Stripe
 * (`CreditCardForm` with `CardElement` and billing fields) or a PayPal notice.
 *
 * Requires Formik context (`CheckoutValues`) and Stripe Elements from a parent.
 * Parent steps own reCAPTCHA, validation schema, and submit handlers.
 *
 * @param props - Component props
 * @param props.paymentType - The currently selected payment type
 * @param props.setPaymentType - Callback function to update the payment type
 */
const CheckoutForm = ({ paymentType, setPaymentType }: CheckoutFormProps) => {
  const { classes } = useStyles();

  // Note: useFormikContext will throw an error if Formik context is not available.
  // This component must be rendered within a Formik provider.
  const { values, errors, touched, validateForm, setFieldValue, setFieldTouched } =
    useFormikContext<CheckoutValues>();

  // Validate form when payment type changes
  useEffect(() => {
    validateForm();
  }, [paymentType, validateForm]);

  /**
   * Handler for radio button changes to update payment type
   */
  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPaymentType(event.target.value as PaymentType);
    },
    [setPaymentType],
  );

  /**
   * Handler for credit card form field changes.
   * Supports both standard React events and imperative value setting.
   *
   * @param fieldName - The name of the form field
   * @param event - The React change event (optional if imperativeValue is provided)
   * @param imperativeValue - Optional value to set imperatively (bypasses event)
   */
  const handleCreditCardForm = useCallback(
    (
      fieldName: string,
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined,
      imperativeValue?: unknown,
    ) => {
      if (fieldName === 'cardInfo') {
        if (imperativeValue !== undefined) {
          setFieldValue('cardInfo', imperativeValue, true);
        }
        return;
      }

      if (imperativeValue !== undefined) {
        setFieldValue(fieldName, imperativeValue, true);
      } else if (event) {
        setFieldValue(fieldName, event.target.value, true);
      }
    },
    [setFieldValue],
  );

  /**
   * Sync DOM value on blur so browser autofill (which often skips React onChange)
   * updates Formik before validation runs. Touch only on blur to avoid flashing
   * required errors while autofill is being applied.
   */
  const handleCreditCardFormBlur = useCallback(
    (
      fieldName: string,
      event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (fieldName === 'cardInfo') return;
      setFieldValue(fieldName, event.target.value, true);
      setFieldTouched(fieldName, true, false);
    },
    [setFieldValue, setFieldTouched],
  );

  const handleCardBlur = useCallback(() => {
    setFieldTouched('cardInfo', true, false);
  }, [setFieldTouched]);

  return (
    <Box flexDirection="column" width="100%" data-component="CheckoutForm">
      <CardSectionHeader sectionTitle={'Payment Method'} />
      <RadioGroup
        id={'paymentType'}
        aria-label="Payment Type"
        name="paymentType"
        value={paymentType}
        onChange={handleRadioChange}
        className={classes.radioGroupContainer}
      >
        <RadioCard
          value="Stripe"
          Icon={IconMDI.CreditCardOutline}
          displayText="Card"
          active={paymentType === 'Stripe'}
        />
        <RadioCard
          value="PayPal"
          Icon={IconMDI.AccountCash}
          displayText="PayPal"
          active={paymentType === 'PayPal'}
        />
        {/* <div className={classes.bitPayContainer}>
                    <RadioCard
                        value="BitPay"
                        Icon={BitPayIcon}
                        displayText="BitPay"
                        active={paymentType === 'BitPay'}
                    />
                </div> */}
      </RadioGroup>
      {paymentType === 'PayPal' && (
        <Typography>PayPal will open in a new window upon checkout.</Typography>
      )}
      {paymentType === 'Stripe' && (
        <CreditCardForm
          handleInputChange={handleCreditCardForm}
          handleInputBlur={handleCreditCardFormBlur}
          handleCardBlur={handleCardBlur}
          values={values}
          errors={errors}
          touched={touched}
        />
      )}
    </Box>
  );
};

/**
 * Props for the CheckoutForm component
 */
export interface CheckoutFormProps {
  /** The currently selected payment type */
  paymentType: PaymentType;
  /** Callback function to update the payment type */
  setPaymentType: (val: PaymentType) => void;
}

export default CheckoutForm;
